import { computed, onMounted, reactive, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';

const VERSION_RE = /^V\d{2}\.\d{2}$/;
const DEFAULT_VERSION = '默认';
const DEFAULT_GROUPS = ['新接口', '旧接口', '手动添加'];

function normalizeRow(row) {
  return {
    funcCode: String(row?.funcCode || ''),
    description: String(row?.description || ''),
    defaultDescription: String(row?.defaultDescription || row?.description || ''),
    enabledVersion: String(row?.enabledVersion || DEFAULT_VERSION),
    latestDeprecated: Boolean(row?.latestDeprecated),
    deprecatedVersion: String(row?.deprecatedVersion || DEFAULT_VERSION),
    group: String(row?.group || ''),
    source: String(row?.source || ''),
    updatedAt: String(row?.updatedAt || ''),
    updatedBy: String(row?.updatedBy || ''),
  };
}

function versionRank(value) {
  const text = String(value || '');
  if (/^\d+$/.test(text)) return Number(text);
  return Number.MAX_SAFE_INTEGER;
}

function validateVersion(value, allowDefault, label) {
  const text = String(value || '').trim();
  if (allowDefault && text === DEFAULT_VERSION) return text;
  if (!VERSION_RE.test(text)) {
    throw new Error(`${label}必须为 ${allowDefault ? '默认 或 ' : ''}Vxx.xx，例如 V44.40`);
  }
  return text;
}

export function useMazdaInterfaceDocs(mazdaPost, serverName) {
  const loading = ref(false);
  const savingFuncCode = ref('');
  const rows = ref([]);
  const redisKey = ref('');
  const redisDb = ref('');
  const keyword = ref('');
  const groupFilter = ref('');
  const statusFilter = ref('');
  const addDialogVisible = ref(false);
  const manualForm = reactive({
    funcCode: '',
    description: '',
    enabledVersion: DEFAULT_VERSION,
    latestDeprecated: false,
    deprecatedVersion: DEFAULT_VERSION,
    group: '手动添加',
    source: '',
  });
  const stats = reactive({
    total: 0,
    legacy: 0,
    current: 0,
    deprecated: 0,
  });

  const groupOptions = computed(() => {
    const set = new Set([...DEFAULT_GROUPS, ...rows.value.map((row) => row.group).filter(Boolean)]);
    return Array.from(set).map((item) => ({ label: item, value: item }));
  });

  const filteredRows = computed(() => {
    const key = keyword.value.trim().toLowerCase();
    return rows.value
      .filter((row) => !groupFilter.value || row.group === groupFilter.value)
      .filter((row) => {
        if (statusFilter.value === 'deprecated') return row.latestDeprecated;
        if (statusFilter.value === 'active') return !row.latestDeprecated;
        return true;
      })
      .filter((row) => {
        if (!key) return true;
        return [
          row.funcCode,
          row.description,
          row.enabledVersion,
          row.deprecatedVersion,
          row.group,
          row.source,
        ].some((value) => String(value || '').toLowerCase().includes(key));
      })
      .sort((a, b) => versionRank(a.funcCode) - versionRank(b.funcCode));
  });

  const applyInfo = (data) => {
    rows.value = Array.isArray(data.items) ? data.items.map(normalizeRow) : [];
    redisKey.value = data.redisKey || '';
    redisDb.value = data.redisDb || '';
    stats.total = Number(data.stats?.total || rows.value.length || 0);
    stats.legacy = Number(data.stats?.legacy || rows.value.filter((row) => row.group === '旧接口').length);
    stats.current = Number(data.stats?.current || rows.value.filter((row) => row.group !== '旧接口').length);
    stats.deprecated = Number(data.stats?.deprecated || rows.value.filter((row) => row.latestDeprecated).length);
  };

  const loadInterfaceDocs = async () => {
    loading.value = true;
    try {
      const response = await mazdaPost('interfaceDocsInfo');
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '读取接口说明失败');
        return;
      }
      applyInfo(data);
    } catch (error) {
      ElMessage.error(`读取接口说明失败: ${error.response?.data?.message || error.message}`);
    } finally {
      loading.value = false;
    }
  };

  const saveRow = async (row) => {
    try {
      const payload = {
        funcCode: String(row.funcCode || '').trim(),
        description: String(row.description || '').trim(),
        enabledVersion: validateVersion(row.enabledVersion, true, '启用版本'),
        latestDeprecated: Boolean(row.latestDeprecated),
        deprecatedVersion: row.latestDeprecated
          ? validateVersion(row.deprecatedVersion, false, '弃用版本')
          : String(row.deprecatedVersion || DEFAULT_VERSION).trim() || DEFAULT_VERSION,
        group: String(row.group || '手动添加').trim(),
        source: String(row.source || '').trim(),
      };

      if (!payload.funcCode) throw new Error('功能号不能为空');
      if (!payload.description) throw new Error('功能说明不能为空');

      savingFuncCode.value = payload.funcCode;
      const response = await mazdaPost('interfaceDocsSave', payload);
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '保存接口说明失败');
        return false;
      }
      applyInfo(data);
      ElMessage.success(data.message || '接口说明已保存');
      return true;
    } catch (error) {
      ElMessage.error(error.response?.data?.message || error.message || '保存接口说明失败');
      return false;
    } finally {
      savingFuncCode.value = '';
    }
  };

  const setDeprecated = (row, value) => {
    row.latestDeprecated = value;
    if (!value && !row.deprecatedVersion) {
      row.deprecatedVersion = DEFAULT_VERSION;
    }
  };

  const resetManualForm = () => {
    manualForm.funcCode = '';
    manualForm.description = '';
    manualForm.enabledVersion = DEFAULT_VERSION;
    manualForm.latestDeprecated = false;
    manualForm.deprecatedVersion = DEFAULT_VERSION;
    manualForm.group = '手动添加';
    manualForm.source = '';
  };

  const openAddDialog = () => {
    resetManualForm();
    addDialogVisible.value = true;
  };

  const submitManualRow = async () => {
    const ok = await saveRow(manualForm);
    if (ok) {
      addDialogVisible.value = false;
      resetManualForm();
    }
  };

  onMounted(loadInterfaceDocs);

  watch(serverName, () => {
    loadInterfaceDocs();
  });

  return {
    loading,
    savingFuncCode,
    rows,
    filteredRows,
    redisKey,
    redisDb,
    keyword,
    groupFilter,
    statusFilter,
    addDialogVisible,
    manualForm,
    stats,
    groupOptions,
    loadInterfaceDocs,
    saveRow,
    setDeprecated,
    openAddDialog,
    submitManualRow,
  };
}
